import type { LinkProps } from "@/infrastructure/router/Link";

/**
 * Make Studio catalog — mirrors the iOS `MakePage` constants:
 *   - `MakeComposerMode` (simple / custom)
 *   - `CreateVocalGender` (none / male / female)
 *   - `LyricsInputMode` (autoDetect / none / file / text)
 *   - `MakeWorkTabDisplayFilter` (all / published / unpublished)
 *   - `IPContentTab` (songs / notes)
 *   - Style tag library (mirrors `CreateView.styleTagLibrary`)
 *
 * Source: Artist/Features/Make/Views/MakePage.swift
 */

export type MakeTab = "create" | "ip" | "upload";
export type CreateComposerMode = "simple" | "custom";
export type CreateVocalGender = "none" | "male" | "female";
export type LyricsInputMode = "autoDetect" | "none" | "file" | "text";
export type IPContentTab = "songs" | "notes";
export type WorkDisplayFilter = "all" | "published" | "unpublished";

export const MAKE_TAB_LABELS: Record<MakeTab, string> = {
	create: "创作",
	ip: "作品",
	upload: "上传",
};

export const CREATE_MODE_LABELS: Record<CreateComposerMode, string> = {
	simple: "简单",
	custom: "自定义",
};

export const VOCAL_GENDER_LABELS: Record<CreateVocalGender, string> = {
	none: "无",
	male: "男声",
	female: "女声",
};

export const LYRICS_MODE_LABELS: Record<LyricsInputMode, string> = {
	autoDetect: "自动检测",
	none: "无歌词",
	file: "文件",
	text: "文本",
};

export const IP_CONTENT_TAB_LABELS: Record<IPContentTab, string> = {
	songs: "歌曲",
	notes: "笔记",
};

export const WORK_FILTER_LABELS: Record<WorkDisplayFilter, string> = {
	all: "全部",
	published: "已发布",
	unpublished: "未发布",
};

/** Music generation model descriptor — mirrors iOS `GenArtModelDescriptor`. */
export interface MakeModelDescriptor {
	readonly id: string;
	readonly providerKey: string;
	readonly providerDisplayName: string;
	readonly modelKey: string;
	readonly displayName: string;
	readonly callToActionTint: string;
	readonly musicGenerationPrice: number;
	readonly lyricsGenerationPrice: number;
	readonly isDefault: boolean;
	readonly supportsCustomMode: boolean;
	readonly supportsInstrumentalToggle: boolean;
	readonly supportsVocalGenderControl: boolean;
	readonly supportsLyricsGeneration: boolean;
}

/**
 * Three-model catalog locked by the iOS golden screenshot
 * (`创作页简单模式.jpeg`). Mirrors `MusicGenerationService.cachedCatalogSeed()`
 * surfaced through `GenArtModelDescriptor.buildMakeCatalog(from:)`.
 */
export const MAKE_MODELS: readonly MakeModelDescriptor[] = [
	{
		id: "minimax-3-0",
		providerKey: "minimax",
		providerDisplayName: "MiniMax",
		modelKey: "minimax_music_3_0",
		displayName: "MiniMax 3.0",
		callToActionTint: "#7373F2",
		musicGenerationPrice: 10,
		lyricsGenerationPrice: 1,
		isDefault: true,
		supportsCustomMode: true,
		supportsInstrumentalToggle: true,
		supportsVocalGenderControl: true,
		supportsLyricsGeneration: true,
	},
	{
		id: "suno-5-5",
		providerKey: "suno",
		providerDisplayName: "Suno",
		modelKey: "suno_v5_5",
		displayName: "Suno v5.5",
		callToActionTint: "#E0589E",
		musicGenerationPrice: 12,
		lyricsGenerationPrice: 1,
		isDefault: false,
		supportsCustomMode: true,
		supportsInstrumentalToggle: true,
		supportsVocalGenderControl: false,
		supportsLyricsGeneration: true,
	},
	{
		id: "udio-1-5",
		providerKey: "udio",
		providerDisplayName: "Udio",
		modelKey: "udio_1_5",
		displayName: "Udio 1.5",
		callToActionTint: "#3DDC97",
		musicGenerationPrice: 8,
		lyricsGenerationPrice: 1,
		isDefault: false,
		supportsCustomMode: false,
		supportsInstrumentalToggle: true,
		supportsVocalGenderControl: false,
		supportsLyricsGeneration: false,
	},
];

export function defaultMakeModel(): MakeModelDescriptor {
	// biome-ignore lint/style/noNonNullAssertion: MAKE_MODELS is a static catalog literal guaranteed to contain at least one entry
	return MAKE_MODELS.find((model) => model.isDefault) ?? MAKE_MODELS[0]!;
}

/** Style tag library — mirrors `CreateView.styleTagLibrary`. */
export const STYLE_TAG_LIBRARY: readonly string[] = [
	"anthemic",
	"atmospheric",
	"ballad",
	"bedroom pop",
	"boom bap",
	"bouncy",
	"bright synths",
	"britpop",
	"cinematic",
	"city pop",
	"cloud rap",
	"club",
	"dark pop",
	"deep house",
	"disco",
	"dream pop",
	"drill",
	"driving bass",
	"drum and bass",
	"dub",
	"edm",
	"electro pop",
	"emotional",
	"epic",
	"ethereal",
	"experimental",
	"female vocal",
	"festival",
	"folk pop",
	"funk",
	"future bass",
	"glitch",
	"gospel",
	"gritty",
	"groovy",
	"guitar solo",
	"hard rock",
	"hip hop",
	"house",
	"hyperpop",
	"indie",
	"indie rock",
	"jazz club",
	"k-pop",
	"latin pop",
	"lo-fi",
	"melancholic",
	"minimal",
	"moody",
	"neo soul",
	"night drive",
	"orchestral",
	"piano",
	"playful",
	"pop punk",
	"progressive",
	"psychedelic",
	"r&b",
	"rap",
	"reggae",
	"retro",
	"romantic",
	"saxophone",
	"shoegaze",
	"singer-songwriter",
	"soul",
	"sparkling arp",
	"summer",
	"sunset",
	"synthwave",
	"tech house",
	"trance",
	"trap",
	"uplifting",
	"vaporwave",
	"warm acoustic",
	"wide chorus",
	"worldbeat",
];

export interface PromptSuggestion {
	readonly id: string;
	readonly caption: string;
	readonly prompt: string;
	readonly imageURL: string;
}

export interface IPPlaylistItem {
	readonly id: string;
	readonly name: string;
	readonly color: string;
	readonly icon: "heart" | "music" | "waveform";
	readonly count: number;
}

export interface IPTrackRow {
	readonly id: string;
	readonly title: string;
	readonly artist: string;
	readonly duration: string;
	readonly isPublic: boolean;
	readonly isPlaying?: boolean;
	readonly artworkGradient: string;
	readonly playbackHref: LinkProps<string>["href"] | null;
}

export interface IPNoteRow {
	readonly id: string;
	readonly title: string;
	readonly excerpt: string;
	readonly updatedAt: string;
}

/** Pick `count` random style tags from the library (deterministic per session). */
export function sampleStyleTags(count: number): string[] {
	const pool = [...STYLE_TAG_LIBRARY];
	const picked: string[] = [];
	for (let i = 0; i < count && pool.length > 0; i += 1) {
		const index = Math.floor(Math.random() * pool.length);
		const tag = pool.splice(index, 1)[0];
		if (tag !== undefined) picked.push(tag);
	}
	return picked;
}
